import type React from 'react';
import { useMemo, useState } from 'react';
import { DotsThreeIcon } from '@phosphor-icons/react';
import {
  Button,
  cn,
  MultiSelect,
  Popover,
  type PopoverPlacement,
  SearchSelect,
  Select,
  type SelectOption,
  Spinner,
  TagSelect,
} from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import { CreateNewSupplier } from '@utility/constants/common';
import { Flag } from './Flag';

/*
 * The legacy Semantic `Dropdown` API, drawn with the kit. Callers keep passing
 * Semantic's props and keep receiving `onChange(event, { ...props, value })`;
 * underneath, a single pick is a kit `Select` (or `SearchSelect` with
 * `search`), a multiple pick a `MultiSelect` (or `TagSelect` when it takes
 * additions or shows its picks as tags).
 */

export interface IDropdownOption {
  text: string;
  value: string | number;
  name?: string;
  /** One flag, or several side by side. */
  flag?: string | string[] | null;
  image?: { src: string; style?: React.CSSProperties };
  content?: React.ReactNode;
  description?: React.ReactNode;
  /** Sits at the end of the option's row, e.g. a badge. */
  trailing?: React.ReactNode;
  disabled?: boolean;
  item?: any;
}

type DropdownScalar = boolean | number | string;
export type DropdownValue = DropdownScalar | DropdownScalar[];

/** What Semantic handed its callbacks: every prop, with the new value. */
export interface IDropdownData {
  /* `any`, as it effectively was under the old index-signature props:
     callers store it in state of their own narrower types. */
  // biome-ignore lint/suspicious/noExplicitAny: legacy callers narrow it themselves.
  value?: any;
  options?: IDropdownOption[];
  name?: string;
  searchQuery?: string;
  // biome-ignore lint/suspicious/noExplicitAny: Semantic passed every prop through.
  [key: string]: any;
}

export interface IDropdownSearchData extends IDropdownData {
  searchQuery: string;
}

type DropdownEvent = React.SyntheticEvent<HTMLElement>;

/* Callbacks are written as methods so handlers typed against Semantic's own
   `DropdownProps` stay assignable. */
export interface ISortedDropdownProps {
  options?: IDropdownOption[];
  /** Sorts options by text. On by default. */
  sorted?: boolean;
  /** `null` and `''` both mean nothing is picked. */
  value?: DropdownValue | null;
  defaultValue?: DropdownValue | null;
  placeholder?: string;
  name?: string;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  'aria-label'?: string;
  /** Names the field for screen readers when there is no `aria-label`. */
  label?: string;
  'data-field-value'?: string;

  onChange?(event: DropdownEvent, data: IDropdownData): void;
  /** Typed text added with `allowAdditions`; runs after `onChange`. */
  onAddItem?(event: DropdownEvent, data: IDropdownData): void;
  onSearchChange?(event: DropdownEvent, data: IDropdownSearchData): void;
  onBlur?(event: DropdownEvent, data: IDropdownData): void;
  onFocus?(event: DropdownEvent, data: IDropdownData): void;

  /** Type to narrow the options. A function replaces the default text match. */
  search?:
    | boolean
    // biome-ignore lint/suspicious/noExplicitAny: callers pass their own option shapes.
    | ((options: any[], query: string) => any[] | undefined);
  multiple?: boolean;
  allowAdditions?: boolean;
  clearable?: boolean;
  loading?: boolean;
  disabled?: boolean;
  error?: boolean;
  required?: boolean;
  fluid?: boolean;
  upward?: boolean;
  noResultsMessage?: React.ReactNode;
  /** Kept for `multiple`: any function switches to a count summary. */
  renderLabel?: (
    item: IDropdownOption,
    index: number,
    defaultLabelProps: Record<string, unknown>,
  ) => unknown;

  /* Accepted for source compatibility; the kit draws these its own way. */
  text?: React.ReactNode;
  selection?: boolean;
  compact?: boolean;
  inline?: boolean;
  labeled?: boolean;
  hint?: React.ReactNode;
  selectOnBlur?: boolean;
  selectOnNavigation?: boolean;
  closeOnBlur?: boolean;
  closeOnChange?: boolean;
  searchInput?: unknown;
  selectedLabel?: unknown;
  additionLabel?: React.ReactNode;
  additionPosition?: 'top' | 'bottom';
  children?: React.ReactNode;
}

function sortOptions(options: IDropdownOption[], sorted: boolean) {
  let sortedOptions = sorted
    ? CommonUtility.sortDropdownOptions(options)
    : options;

  const manualSupplierInput = sortedOptions.find(
    op => op.value === CreateNewSupplier,
  );

  if (manualSupplierInput?.value) {
    sortedOptions = sortedOptions.filter(
      so => so.value !== manualSupplierInput.value,
    );
    sortedOptions.unshift({
      text: 'Create New Supplier',
      value: CreateNewSupplier,
      image: { src: '/images/icon-plus.svg' },
    });
  }

  return sortedOptions;
}

const keyOf = (value: unknown) => String(value);

function leadingOf(option: IDropdownOption) {
  if (option.image?.src) {
    return (
      <img
        src={option.image.src}
        alt=""
        className="sk:size-4.5 sk:object-contain"
        style={option.image.style ? { ...option.image.style, margin: 0 } : {}}
      />
    );
  }
  if (option.flag) {
    const flags = Array.isArray(option.flag) ? option.flag : [option.flag];
    return (
      <span className="sk:inline-flex sk:items-center sk:gap-1">
        {flags.map(flag => (
          <Flag key={flag} name={flag} className="sk:mr-0" />
        ))}
      </span>
    );
  }
  return undefined;
}

function toKitOption(option: IDropdownOption): SelectOption {
  return {
    value: keyOf(option.value),
    label:
      typeof option.text === 'string' || typeof option.text === 'number'
        ? String(option.text)
        : String(option.name ?? option.value),
    description:
      typeof option.description === 'string' ? option.description : undefined,
    leading: leadingOf(option),
    trailing: option.trailing,
  };
}

/* Semantic handed over the DOM event; the kit reports values only. Callers
   read `data`, so a stand-in with the usual methods is enough. */
function syntheticEvent(type: string, name?: string, value?: unknown) {
  const target = { name, value };
  return {
    type,
    target,
    currentTarget: target,
    nativeEvent: undefined,
    preventDefault: () => {},
    stopPropagation: () => {},
    persist: () => {},
    isDefaultPrevented: () => false,
    isPropagationStopped: () => false,
  } as unknown as DropdownEvent;
}

const isEmpty = (value: unknown) =>
  value === undefined || value === null || value === '';

export function SortedDropdown(props: ISortedDropdownProps) {
  const {
    options = [],
    sorted = true,
    value: valueProp,
    defaultValue,
    placeholder,
    name,
    id,
    className,
    style,
    'aria-label': ariaLabelProp,
    label,
    'data-field-value': dataFieldValue,
    onChange,
    onAddItem,
    onSearchChange,
    onBlur,
    onFocus,
    search,
    multiple,
    allowAdditions,
    clearable,
    loading,
    disabled,
    error,
    required,
    fluid,
    upward,
    noResultsMessage,
    renderLabel,
  } = props;

  const sortedOptions = useMemo(
    () => sortOptions(options, sorted),
    [options, sorted],
  );
  const kitOptions = useMemo(
    () => sortedOptions.filter(option => !option.disabled).map(toKitOption),
    [sortedOptions],
  );

  /* Semantic stays uncontrolled until `value` is passed. */
  const [ownValue, setOwnValue] = useState<DropdownValue | null | undefined>(
    defaultValue,
  );
  const value = valueProp !== undefined ? valueProp : ownValue;

  const fromKey = (key: string): DropdownScalar => {
    const match = sortedOptions.find(option => keyOf(option.value) === key);
    return match ? match.value : key;
  };

  const dataFor = (next: DropdownValue | null | undefined): IDropdownData => ({
    ...props,
    options: sortedOptions,
    value: next,
  });

  const emit = (next: DropdownValue) => {
    if (valueProp === undefined) {
      setOwnValue(next);
    }
    onChange?.(syntheticEvent('change', name, next), dataFor(next));
  };

  const filter =
    typeof search === 'function'
      ? (() => {
          let lastQuery: string | null = null;
          let kept = new Set<string>();
          return (option: SelectOption, query: string) => {
            if (query !== lastQuery) {
              lastQuery = query;
              kept = new Set(
                ((search(sortedOptions, query) ?? []) as IDropdownOption[]).map(
                  match => keyOf(match.value),
                ),
              );
            }
            return kept.has(option.value);
          };
        })()
      : undefined;

  const ariaLabel =
    ariaLabelProp ||
    label ||
    (typeof placeholder === 'string' && placeholder) ||
    name;
  const placement: PopoverPlacement = upward ? 'top start' : 'bottom start';
  const small = /\bsmall\b/.test(className ?? '');
  const emptyMessage =
    typeof noResultsMessage === 'string' ? noResultsMessage : undefined;
  const invalid = error
    ? 'sk:inset-shadow-[0_0_0_1px_var(--sk-danger-fg)]'
    : undefined;
  const control = cn('sk:w-full', small && 'sk:h-6.5', invalid);
  const handleFocus = () =>
    onFocus?.(syntheticEvent('focus', name), dataFor(value));
  const handleBlur = () =>
    onBlur?.(syntheticEvent('blur', name), dataFor(value));
  const handleInput = (query: string) =>
    onSearchChange?.(syntheticEvent('change', name, query), {
      ...dataFor(value),
      searchQuery: query,
    });

  let field: React.ReactNode;
  /* `Select` and `MultiSelect` have no loading state of their own. */
  let overlaySpinner = false;

  if (multiple) {
    const values = (
      Array.isArray(value) ? value : isEmpty(value) ? [] : [value]
    ).map(keyOf);
    const emitKeys = (keys: string[]) => emit(keys.map(fromKey));

    overlaySpinner = Boolean(loading && renderLabel && !allowAdditions);
    field =
      renderLabel && !allowAdditions ? (
        <MultiSelect
          aria-label={ariaLabel || 'Select'}
          options={kitOptions}
          values={values}
          onChange={emitKeys}
          placeholder={placeholder}
          isDisabled={disabled}
          className={control}
        />
      ) : (
        <TagSelect
          aria-label={ariaLabel || 'Select'}
          options={kitOptions}
          values={values}
          onChange={emitKeys}
          allowsCustomValue={allowAdditions}
          onCreate={typed =>
            onAddItem?.(syntheticEvent('change', name, typed), dataFor(typed))
          }
          onInputChange={handleInput}
          onFocus={handleFocus}
          onBlur={handleBlur}
          filter={filter}
          placeholder={placeholder}
          emptyMessage={emptyMessage}
          isLoading={loading}
          isDisabled={disabled}
          isInvalid={error}
          isRequired={required}
          placement={placement}
          className={cn(small && 'sk:min-h-6.5', invalid)}
        />
      );
  } else if (search) {
    field = (
      <SearchSelect
        aria-label={ariaLabel || 'Select'}
        options={kitOptions}
        value={isEmpty(value) ? '' : keyOf(value)}
        onChange={key => emit(key === '' ? '' : fromKey(key))}
        onInputChange={handleInput}
        onFocus={handleFocus}
        onBlur={handleBlur}
        filter={filter}
        placeholder={placeholder}
        emptyMessage={emptyMessage}
        isClearable={clearable}
        isLoading={loading}
        isDisabled={disabled}
        isInvalid={error}
        isRequired={required}
        placement={placement}
        className={cn(small && 'sk:h-6.5', invalid)}
      />
    );
  } else {
    overlaySpinner = Boolean(loading);
    field = (
      <Select
        aria-label={ariaLabel || 'Select'}
        options={kitOptions}
        value={isEmpty(value) ? '' : keyOf(value)}
        onChange={key => emit(key === '' ? '' : fromKey(key))}
        onBlur={handleBlur}
        placeholder={placeholder}
        allowsEmptySelection={clearable}
        isDisabled={disabled}
        isInvalid={error}
        isRequired={required}
        validationBehavior="aria"
        placement={placement}
        className={control}
      />
    );
  }

  return (
    <span
      id={id}
      style={style}
      data-field-value={dataFieldValue}
      className={cn(
        'sk:relative sk:inline-flex sk:max-w-full sk:align-middle sk:[&>*]:min-w-0 sk:[&>*]:flex-1',
        small ? 'sk:min-w-30' : 'sk:min-w-36',
        /* Semantic stretched a dropdown to its form field; so does this. */
        'sk:[.field>&]:flex sk:[.field>&]:w-full',
        fluid && 'sk:flex sk:w-full',
        className,
      )}
    >
      {field}
      {overlaySpinner ? (
        <Spinner
          size="sm"
          className="sk:pointer-events-none sk:absolute sk:top-1/2 sk:right-2.5 sk:-translate-y-1/2 sk:rounded-full sk:bg-surface sk:text-fg-secondary"
        />
      ) : null}
    </span>
  );
}

export const Dropdown = SortedDropdown;

export interface ICenterDropdownStyleProps extends ISortedDropdownProps {
  loading?: boolean;
  className?: string;
}

/** The everyday form dropdown. */
export function CenterDropdownStyle(props: ICenterDropdownStyleProps) {
  return <SortedDropdown {...props} />;
}

/* Typed as Semantic's own props were for this export, value included, so its
   callers' existing type annotations keep holding. */
export interface ICountryDropdownData extends IDropdownData {
  value?: DropdownValue;
}

export interface ICountryDropdownProps
  extends Omit<ISortedDropdownProps, 'value' | 'onChange' | 'text'> {
  value?: DropdownValue;
  onChange?(event: DropdownEvent, data: ICountryDropdownData): void;
  text?: string;
}

/** A dropdown whose options carry flags or logos, a little wider. */
export function CountryDropdownStyle({
  className,
  ...props
}: ICountryDropdownProps) {
  return <SortedDropdown {...props} className={cn('sk:min-w-44', className)} />;
}

export const DropdownCountryStyle = CountryDropdownStyle;

/** A status picker; the same control as `CenterDropdownStyle`. */
export function StatusDropdown(props: ICenterDropdownStyleProps) {
  return <SortedDropdown {...props} />;
}

export interface IActionDropdownProps {
  children?: React.ReactNode;
  className?: string;
  /** Replaces the ellipsis with a labelled trigger. */
  text?: React.ReactNode;
  /** A custom trigger; drawn inside the kit button. */
  trigger?: React.ReactNode;
  /** A node replaces the ellipsis; Semantic icon names are ignored. */
  icon?: React.ReactNode;
  /** `left` opens the panel towards the left, as Semantic did. */
  direction?: 'left' | 'right';
  upward?: boolean;
  disabled?: boolean;
  loading?: boolean;
  'aria-label'?: string;

  /* Accepted for source compatibility. `open` was always `false` with a
     hover-opened `simple` dropdown; the kit opens on click instead. */
  open?: boolean;
  button?: boolean;
  simple?: boolean;
  floating?: boolean;
  pointing?: boolean | string;
}

const ACTION_ITEMS = [
  'sk:flex sk:min-w-44 sk:flex-col sk:items-stretch sk:gap-1',
  /* Children are legacy buttons marked `dropdown-action`; these make them kit
     menu rows. `!` outranks the buttons' own important colours. */
  'sk:[&_.dropdown-action]:m-0! sk:[&_.dropdown-action]:flex! sk:[&_.dropdown-action]:h-auto! sk:[&_.dropdown-action]:min-h-0!',
  'sk:[&_.dropdown-action]:w-full! sk:[&_.dropdown-action]:items-center! sk:[&_.dropdown-action]:justify-start! sk:[&_.dropdown-action]:gap-2!',
  'sk:[&_.dropdown-action]:rounded-option! sk:[&_.dropdown-action]:border-0! sk:[&_.dropdown-action]:px-3! sk:[&_.dropdown-action]:py-2!',
  'sk:[&_.dropdown-action]:bg-transparent! sk:[&_.dropdown-action]:text-left sk:[&_.dropdown-action]:text-dense! sk:[&_.dropdown-action]:font-normal!',
  'sk:[&_.dropdown-action]:text-fg! sk:[&_.dropdown-action]:shadow-none! sk:[&_.dropdown-action]:whitespace-nowrap',
  'sk:[&_.dropdown-action:hover]:bg-fill-quaternary! sk:[&_.dropdown-action:disabled]:cursor-not-allowed sk:[&_.dropdown-action:disabled]:text-fg-tertiary!',
  'sk:[&_.dropdown-action>img]:size-4.5 sk:[&_.dropdown-action>img]:object-contain sk:[&_.dropdown-action>svg]:size-4.5 sk:[&_.dropdown-action>svg]:shrink-0',
  'sk:[&_.dropdown-danger-action]:text-danger! sk:[&_.dropdown-danger-action:hover]:bg-danger-bg!',
].join(' ');

/** The column of actions inside an `ActionDropdown`. */
export function ActionDropdownMenu({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return <div className={cn(ACTION_ITEMS, className)}>{children}</div>;
}

/** A row's "more actions" button, opening its `ActionDropdownMenu`. */
export function ActionDropdown({
  children,
  className,
  text,
  trigger,
  icon,
  direction,
  upward,
  disabled,
  loading,
  'aria-label': ariaLabel,
}: IActionDropdownProps) {
  const label = text ?? trigger;
  const outlined = /\b(border|white)-action\b/.test(className ?? '');
  const side = direction === 'right' ? 'start' : 'end';
  const placement: PopoverPlacement = upward ? `top ${side}` : `bottom ${side}`;
  const glyph =
    icon && typeof icon !== 'string' ? (
      icon
    ) : (
      <DotsThreeIcon size={16} weight="bold" />
    );

  return (
    <Popover
      aria-label={ariaLabel ?? 'Actions'}
      placement={placement}
      trigger={
        <Button
          aria-label={label ? undefined : (ariaLabel ?? 'Actions')}
          variant={outlined ? 'pill' : 'primary'}
          shape={label ? 'default' : 'circle'}
          isDisabled={disabled || loading}
          className={className}
        >
          {loading ? <Spinner size="sm" /> : (label ?? glyph)}
        </Button>
      }
    >
      {({ close }) => (
        // biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: closes after a child action runs; the children are the interactive elements.
        <div
          onClick={event => {
            const action = (event.target as HTMLElement).closest(
              'button, a, [role="menuitem"], .dropdown-action',
            );
            if (
              action &&
              !action.matches(':disabled, [aria-disabled="true"]')
            ) {
              close();
            }
          }}
        >
          {children}
        </div>
      )}
    </Popover>
  );
}
